import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, Save, RotateCcw } from 'lucide-react';
import apiService from '../../../apiService';
import { Card, Spinner } from '../financeShared';

const FRAUD_FIELDS = [
  { key: 'rapidWithdrawalsCount', label: 'Retraits rapprochés — seuil de déclenchement', help: 'Nombre de retraits par un même partenaire qui déclenche l\'alerte.', unit: 'retraits' },
  { key: 'rapidWithdrawalsDays', label: 'Retraits rapprochés — fenêtre', help: 'Période d\'observation pour compter les retraits rapprochés.', unit: 'jours' },
  { key: 'failedWithdrawalsCount', label: 'Retraits échoués répétés — seuil', help: 'Nombre d\'échecs de retrait qui déclenche l\'alerte.', unit: 'échecs' },
  { key: 'negSupplierThreshold', label: 'Commissions impayées — seuil', help: 'Solde fournisseur négatif (magnitude) considéré comme important.', unit: 'CDF' },
  { key: 'refundThreshold', label: 'Remboursements clients — seuil', help: 'Total de remboursements cumulés signalant un client à surveiller.', unit: 'CDF' },
];

export default function SettingsTab() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState('');
  const [fraud, setFraud] = useState({});
  const [defaults, setDefaults] = useState({});
  const [cats, setCats] = useState([]);
  const [newCat, setNewCat] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiService.get('/admin/finance/settings', { userType: 'admin' });
      if (res?.success) { setFraud(res.fraud || {}); setDefaults(res.fraudDefaults || {}); setCats(res.expenseCategories || []); }
    } catch (e) { console.error('finance settings:', e); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setF = (k, v) => setFraud(f => ({ ...f, [k]: v }));
  const setCatLabel = (i, v) => setCats(cs => cs.map((c, idx) => idx === i ? { ...c, label: v } : c));
  const removeCat = (i) => setCats(cs => cs.filter((_, idx) => idx !== i));
  const addCat = () => { const l = newCat.trim(); if (!l) return; setCats(cs => [...cs, { key: '', label: l }]); setNewCat(''); };

  const save = async () => {
    setSaving(true); setSavedMsg('');
    try {
      const fraudOut = {};
      for (const f of FRAUD_FIELDS) { const n = Number(fraud[f.key]); if (Number.isFinite(n) && n >= 0) fraudOut[f.key] = Math.round(n); }
      const expenseCategories = cats.filter(c => (c.label || '').trim()).map(c => ({ key: c.key || '', label: c.label.trim() }));
      if (expenseCategories.length === 0) { alert('Au moins une catégorie de dépense est requise.'); setSaving(false); return; }
      const res = await apiService.patch('/admin/finance/settings', { fraud: fraudOut, expenseCategories }, { userType: 'admin' });
      if (res?.success) { setFraud(res.fraud || {}); setCats(res.expenseCategories || []); setSavedMsg('Paramètres enregistrés ✓'); setTimeout(() => setSavedMsg(''), 3000); }
    } catch (e) { console.error('save settings:', e); alert('Erreur lors de l\'enregistrement'); } finally { setSaving(false); }
  };

  const resetFraud = () => setFraud({ ...defaults });

  if (loading) return <Spinner />;

  return (
    <>
      <div style={{ background: '#F4F5F7', borderRadius: '10px', padding: '0.85rem 1rem', marginBottom: '1.5rem', color: '#414852', fontSize: '0.82rem' }}>
        Ces paramètres pilotent l'onglet <strong>Anti-fraude</strong> et les <strong>catégories de dépenses</strong>. Toute modification est tracée dans le journal d'audit et prend effet immédiatement, sans redéploiement.
      </div>

      {/* Seuils anti-fraude */}
      <Card style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3 style={{ margin: 0, color: '#1E2229', fontSize: '1.05rem', fontWeight: 700 }}>Seuils anti-fraude</h3>
          <button className="btn-secondary" onClick={resetFraud} style={{ padding: '0.35rem 0.8rem', display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.8rem' }}><RotateCcw size={14} /> Valeurs par défaut</button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem' }}>
          {FRAUD_FIELDS.map(f => (
            <div key={f.key}>
              <label style={{ fontSize: '0.82rem', fontWeight: 600, color: '#2C313A', display: 'block', marginBottom: 3 }}>{f.label}</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <input type="number" min="0" value={fraud[f.key] ?? ''} onChange={e => setF(f.key, e.target.value)}
                  style={{ width: '100%', padding: '0.45rem 0.6rem', borderRadius: '6px', border: '1px solid #E9EBEF', fontSize: '0.9rem' }} />
                <span style={{ fontSize: '0.75rem', color: '#9AA0A8', whiteSpace: 'nowrap' }}>{f.unit}</span>
              </div>
              <p style={{ fontSize: '0.72rem', color: '#9AA0A8', margin: '3px 0 0' }}>{f.help}</p>
            </div>
          ))}
        </div>
      </Card>

      {/* Catégories de dépenses */}
      <Card style={{ marginBottom: '2rem' }}>
        <h3 style={{ margin: '0 0 1rem', color: '#1E2229', fontSize: '1.05rem', fontWeight: 700 }}>Catégories de dépenses</h3>
        <p style={{ fontSize: '0.78rem', color: '#9AA0A8', margin: '0 0 1rem' }}>
          Modifiez les libellés, ajoutez vos postes (carburant, frais SerdiPay, assurances…) ou supprimez ceux que vous n'utilisez pas. Les dépenses déjà enregistrées dans une catégorie supprimée restent visibles sous leur clé technique.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
          {cats.map((c, i) => (
            <div key={c.key || `new-${i}`} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <input value={c.label} onChange={e => setCatLabel(i, e.target.value)}
                style={{ flex: 1, padding: '0.45rem 0.6rem', borderRadius: '6px', border: '1px solid #E9EBEF', fontSize: '0.9rem' }} />
              {c.key && <code style={{ fontSize: '0.7rem', color: '#9AA0A8' }}>{c.key}</code>}
              <button onClick={() => removeCat(i)} title="Supprimer" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626' }}><Trash2 size={16} /></button>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: '1rem' }}>
          <input value={newCat} onChange={e => setNewCat(e.target.value)} onKeyDown={e => e.key === 'Enter' && addCat()} placeholder="Nouvelle catégorie (ex. Carburant)"
            style={{ flex: 1, maxWidth: 320, padding: '0.45rem 0.6rem', borderRadius: '6px', border: '1px solid #E9EBEF', fontSize: '0.9rem' }} />
          <button className="btn-secondary" onClick={addCat} style={{ padding: '0.45rem 0.9rem', display: 'flex', alignItems: 'center', gap: 5 }}><Plus size={15} /> Ajouter</button>
        </div>
      </Card>

      {/* Enregistrer */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <button className="btn-primary" onClick={save} disabled={saving} style={{ padding: '0.6rem 1.5rem', display: 'flex', alignItems: 'center', gap: 7 }}>
          <Save size={16} /> {saving ? 'Enregistrement…' : 'Enregistrer les paramètres'}
        </button>
        {savedMsg && <span style={{ color: '#10B981', fontWeight: 600, fontSize: '0.88rem' }}>{savedMsg}</span>}
      </div>
    </>
  );
}
